import React, { useState } from 'react';
import CommonButton from './CommonButton';

/**
 * CommonTabs — a group of tab buttons built on the shared CommonButton
 * (default theme button). Layout comes from the `.common_tabs` class in
 * src/styles/theme.css (token-based) — no inline styles.
 *
 * Pass labels and their click handlers as parallel arrays (index-aligned):
 *
 *   <CommonTabs
 *       tabs={['Policies (5)', 'Modes', 'Violations (2)']}
 *       handlers={[() => setView('policies'), () => setView('modes'), () => setView('violations')]}
 *   />
 *
 * The selected tab renders in the navy-gradient primary style; every other
 * tab renders in the muted `white` variant so the selection always reads at
 * a glance.
 *
 * Selection can be CONTROLLED or UNCONTROLLED:
 *
 *  • Controlled — pass `active`. The selected tab matches by index, or by
 *    value when you also pass a parallel `values` array (handy when state is
 *    a string id that differs from the label):
 *
 *      <CommonTabs
 *          tabs={['By Endpoint', 'By Policy']}
 *          values={['by_endpoint', 'by_policy']}
 *          active={view}                              // 'by_endpoint' | 'by_policy'
 *          handlers={[() => setView('by_endpoint'), () => setView('by_policy')]}
 *      />
 *
 *  • Uncontrolled — omit `active`. CommonTabs tracks its own selection,
 *    defaulting to the first tab, and updates it on click (still calling the
 *    matching handler). Great for a self-contained action/tab strip.
 *    Use `defaultIndex` to start on a different tab.
 *
 * Props:
 *   tabs            : string[]            — tab labels
 *   handlers        : Function[]          — click handlers (index-aligned)
 *   values          : any[]               — optional per-tab values for `active` matching
 *   active          : number | any        — controlled selection (index, or value with `values`)
 *   defaultIndex    : number              — initial selection when uncontrolled (default 0)
 *   inactiveVariant : CommonButton variant for non-selected tabs (default 'white')
 *   className       : extra class on the wrapper
 */
function CommonTabs({ tabs = [], handlers = [], values = [], active, defaultIndex = 0, className, inactiveVariant = 'white' }) {
    const isControlled = active !== undefined && active !== null;
    // Own the selection when the caller doesn't drive it via `active`.
    const [internalIndex, setInternalIndex] = useState(defaultIndex);

    const isSelected = (i) => (
        isControlled
            ? (values.length ? values[i] === active : active === i)
            : internalIndex === i
    );

    const handleClick = (i) => {
        if (!isControlled) setInternalIndex(i);
        if (handlers[i]) handlers[i]();
    };

    return (
        <div className={`common_tabs ${className || ''}`.trim()}>
            {tabs.map((label, i) => {
                const selected = isSelected(i);
                // Selected tab → primary (navy gradient). Everything else →
                // the muted variant (white) so the active tab stands out.
                const variant = selected ? undefined : inactiveVariant;
                return (
                    <CommonButton
                        key={i}
                        text={label}
                        onClick={() => handleClick(i)}
                        variant={variant}
                        className={selected ? 'cmn_tab--active' : ''}
                    />
                );
            })}
        </div>
    );
}

export default CommonTabs;
